import { useEffect, useState, type FormEvent } from "react";
import { ArrowUpRight, Heart, Mountain, Star } from "lucide-react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";

interface ReviewInfo {
  status: string;
  publicDestination: string;
  publicReviewUrl: string | null;
  expiresAt: string;
  language: string;
  propertyName: string;
}

export function ReviewPage() {
  const { token } = useParams();
  const [info, setInfo] = useState<ReviewInfo | null>(null);
  const [rating, setRating] = useState(0);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api<ReviewInfo>(`/api/public/reviews/${token}`).then(setInfo).catch((caught) => setError(caught instanceof Error ? caught.message : "Link unavailable."));
  }, [token]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api(`/api/public/reviews/${token}/feedback`, { method: "POST", body: JSON.stringify({ rating, comment: form.get("comment"), language: info?.language ?? "en" }) });
      setSent(true);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not save feedback."); }
  }

  if (error && !info) return <div className="review-page"><div className="review-card"><Mountain className="review-logo" /><h1>This link is unavailable</h1><p>{error}</p></div></div>;
  if (!info) return <div className="review-page"><div className="review-card"><span className="loading-spinner" /></div></div>;
  const spanish = info.language.startsWith("es");
  const publicLabel = info.publicDestination === "google" ? "Google" : info.publicDestination.charAt(0).toUpperCase() + info.publicDestination.slice(1);

  return (
    <div className="review-page">
      <div className="review-card">
        <Mountain className="review-logo" />
        <span className="eyebrow">{info.propertyName}</span>
        <h1>{spanish ? "Gracias por hospedarte con nosotros" : "Thank you for staying with us"}</h1>
        <p>{spanish ? "Tu opinión ayuda a otros viajeros y también nos ayuda a mejorar." : "Your experience helps other travelers and helps our team improve."}</p>
        <div className="public-review-box">
          <Heart />
          <div><strong>{spanish ? `Comparte tu experiencia en ${publicLabel}` : `Share your experience on ${publicLabel}`}</strong><span>{spanish ? "Esta opción está disponible para todos nuestros huéspedes." : "This option is available to every guest."}</span></div>
          {info.publicReviewUrl ? <a href={info.publicReviewUrl} target="_blank" rel="noreferrer" className="icon-button"><ArrowUpRight /></a> : <span className="review-instructions">{spanish ? "Abre la solicitud de reseña de la plataforma donde reservaste." : "Open the review request from the platform you booked with."}</span>}
        </div>
        {!sent ? <form className="feedback-form" onSubmit={submit}>
          <h2>{spanish ? "Comentarios privados para nuestro equipo" : "Private feedback for our team"}</h2>
          <div className="rating-row" role="group" aria-label="Rating">{[1, 2, 3, 4, 5].map((value) => <button type="button" key={value} onClick={() => setRating(value)} aria-label={`${value} stars`} className={rating >= value ? "selected" : ""}><Star /></button>)}</div>
          <textarea name="comment" rows={4} placeholder={spanish ? "¿Qué salió bien? ¿Qué podemos mejorar?" : "What went well? What could we improve?"} />
          {error && <p className="form-error">{error}</p>}
          <button className="button button-primary" disabled={!rating}>{spanish ? "Enviar al equipo" : "Send to the team"}</button>
        </form> : <div className="feedback-thanks"><Heart /><h2>{spanish ? "Gracias por tus comentarios" : "Thank you for your feedback"}</h2><p>{spanish ? "Nuestro equipo lo recibió." : "Our team has received it."}</p></div>}
      </div>
    </div>
  );
}
